"use client";

import { useEffect, useRef } from "react";
// The locale-aware router, NOT next/navigation. The plain one pushes
// /order/… without the /sl prefix, which is a 404 — the button appears to
// work and lands the customer on "Page not found" one step from paying.
import { useRouter } from "src/i18n/navigation";
import { useCartDrawer } from "@contexts";

/**
 * Give the cart drawer a way out to checkout.
 *
 * The drawer shows two different buttons. "Continue ordering" only closes it —
 * it means "keep shopping". The one that actually goes to checkout is rendered
 * ONLY when a page has registered a checkout function here.
 *
 * A page that forgets this looks completely fine: the drawer still opens, the
 * line is still in it, and there is still a button underneath that reads, in
 * Slovenian, "Nadaljuj z naročanjem" — continue with ordering. Clicking it
 * closes the drawer. The artwork and canvas pages shipped that way, so nothing
 * in the entire catalogue could be bought; it looked like a mobile bug because
 * that is where it happened to be noticed.
 *
 * Hence a hook rather than a copied effect: three pages needed this and two of
 * them did not have it.
 *
 * @param target the order to route to, or null before anything is in the cart.
 */
export function useCheckoutRegistration(
  target: { orderId: string; guestToken: string } | null,
): void {
  const { setCheckoutFunction } = useCartDrawer();
  const router = useRouter();

  // next-intl's router is a new object every render; keeping it in a ref stops
  // the effect below re-running forever.
  const routerRef = useRef(router);
  routerRef.current = router;

  useEffect(() => {
    if (!target) {
      setCheckoutFunction(undefined);
      return;
    }
    const { orderId, guestToken } = target;
    const go = () => {
      const params = new URLSearchParams();
      params.set("guest_session_token", guestToken);
      // photo-options auto-skips to delivery for orders with no photos, which
      // is every artwork order.
      routerRef.current.push(
        `/order/${orderId}/photo-options?${params.toString()}`,
      );
    };
    setCheckoutFunction(() => go);
    return () => setCheckoutFunction(undefined);
    // routerRef and setCheckoutFunction are stable.
  }, [target?.orderId, target?.guestToken, setCheckoutFunction]);
}
